import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  TextField, Typography,
} from '@mui/material';
import { AttachFileRounded as AttachIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../../../utils/functions/api';
import { showAlert } from '../../../../utils/functions/globalDialog';
import { formatCurrency } from '../../utils/format';

const MAX_FILE_MB = 10; // ตรงกับเพดานของ POST /debt-note/upload (debtNote.routes.js)

// บันทึกการคืนเงิน 1 ครั้งของรายการเจ้าหนี้/ลูกหนี้ (DebtNote) — ไฟล์หลักฐานอัปขึ้นทีละไฟล์ทันทีที่เลือก
// (POST /debt-note/upload) แล้วส่งแค่ URL ไปกับ POST /debt-note/:id/repayments ตอนกดบันทึก
export default function DebtRepaymentDialog({ note, remaining, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [memo, setMemo] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!note) return;
    setAmount('');
    setDate(dayjs().format('YYYY-MM-DD'));
    setMemo('');
    setFiles([]);
  }, [note]);

  const handlePickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const tooBig = picked.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) {
      showAlert(`ไฟล์ "${tooBig.name}" ใหญ่เกิน ${MAX_FILE_MB} MB`);
      return;
    }
    setUploading(true);
    try {
      for (const file of picked) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post(`${process.env.REACT_APP_API_URL}/debt-note/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setFiles((prev) => [...prev, { name: file.name, url: data.url }]);
      }
    } finally {
      setUploading(false);
    }
  };

  const amountNum = Number(amount);
  const canSave = amountNum > 0 && !!date && !uploading && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/debt-note/${note._id}/repayments`, {
        amount: amountNum,
        date: dayjs(date).toDate(),
        note: memo,
        files,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!note} onClose={() => !saving && !uploading && onClose()} fullWidth maxWidth="sm">
      <DialogTitle component="div" sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>บันทึกการคืนเงิน</Typography>
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.3 }} noWrap>{note?.party}</Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5} sx={{ pt: 1.5 }}>
          {remaining != null && remaining > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>ยอดคงเหลือ</Typography>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(remaining)}
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => setAmount(String(remaining))}>
                คืนเต็มจำนวน
              </Button>
            </Stack>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="จำนวนเงินที่คืน (บาท)"
              type="number"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              sx={{ flex: 1 }}
            />
            <TextField
              label="วันที่คืน"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 190 } }}
            />
          </Stack>
          <TextField
            label="หมายเหตุ"
            multiline
            minRows={3}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
          />
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              multiple
              hidden
              onChange={handlePickFiles}
            />
            <Button
              variant="outlined"
              fullWidth
              startIcon={uploading ? <CircularProgress size={18} /> : <AttachIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || saving}
              sx={{ py: 1.25, borderStyle: 'dashed' }}
            >
              {uploading ? 'กำลังอัปโหลด...' : `แนบหลักฐาน (รูป / PDF ไม่เกิน ${MAX_FILE_MB} MB ต่อไฟล์)`}
            </Button>
            {files.length > 0 && (
              <Stack direction="row" sx={{ mt: 1.25, flexWrap: 'wrap', gap: 1 }}>
                {files.map((f) => (
                  <Chip
                    key={f.url}
                    label={f.name}
                    onDelete={() => setFiles((prev) => prev.filter((x) => x.url !== f.url))}
                    sx={{ maxWidth: '100%' }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} disabled={saving || uploading}>ยกเลิก</Button>
        <Button variant="contained" color="success" disableElevation onClick={handleSave} disabled={!canSave} sx={{ px: 3 }}>
          {saving ? 'กำลังบันทึก...' : 'บันทึกการคืน'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
